import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import TextInput from '../ui/TextInput';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { UserIcon, LockIcon, CogIcon } from '../ui/Icons';
import Preferences from '../Preferences/Preferences';
import ApplicationSettingsForm from '../../modules/settings/ApplicationSettingsForm';
import SidebarCustomizer from '../Layout/SidebarCustomizer';
import { getSidebarItems } from '../Layout/config/SidebarItems';
import { useModuleEntitlements } from '../../modules/module-management';
import {
  fetchSidebarLayout,
  getCachedSidebarLayout,
  type SidebarLayout,
} from '../../utils/sidebarLayout';
import { cn } from '../../lib/utils';

interface ProfileData {
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userDisplayName?: string;
}

interface PasswordData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

type SettingsSection = 'profile' | 'password' | 'preferences' | 'menu' | 'application';

const MenuIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const BuildingIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const BASE_SECTIONS: Array<{
  id: SettingsSection;
  label: string;
  description: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}> = [
  {
    id: 'profile',
    label: 'Profile',
    description: 'Name and contact details',
    icon: <UserIcon className="w-4 h-4" />,
  },
  {
    id: 'password',
    label: 'Password',
    description: 'Update account security',
    icon: <LockIcon className="w-4 h-4" />,
  },
  {
    id: 'preferences',
    label: 'Preferences',
    description: 'Theme, tables, and views',
    icon: <CogIcon className="w-4 h-4" />,
  },
  {
    id: 'menu',
    label: 'Customize menu',
    description: 'Reorder groups and sections',
    icon: <MenuIcon className="w-4 h-4" />,
  },
  {
    id: 'application',
    label: 'Application',
    description: 'Company-wide app settings',
    icon: <BuildingIcon className="w-4 h-4" />,
    adminOnly: true,
  },
];

const isSettingsSection = (value: string | null): value is SettingsSection =>
  value === 'profile' ||
  value === 'password' ||
  value === 'preferences' ||
  value === 'menu' ||
  value === 'application';

const ProfileSettings: React.FC = () => {
  const { user, refreshUser, can, permissionsLoaded } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const { entitlements } = useModuleEntitlements();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.role === 'ADMIN';
  const sections = BASE_SECTIONS.filter((section) => !section.adminOnly || isAdmin);
  const tabFromUrl = searchParams.get('tab');
  const initialTab: SettingsSection =
    isSettingsSection(tabFromUrl) && (tabFromUrl !== 'application' || isAdmin)
      ? tabFromUrl
      : 'profile';
  const [activeTab, setActiveTab] = useState<SettingsSection>(initialTab);
  const [sidebarLayout, setSidebarLayout] = useState<SidebarLayout | null>(() => getCachedSidebarLayout());

  const canView = useMemo(
    () => (permissionsLoaded ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded]
  );
  const sidebarBaseItems = useMemo(
    () => getSidebarItems(user?.role, entitlements, canView),
    [user?.role, entitlements, canView]
  );

  useEffect(() => {
    let cancelled = false;
    fetchSidebarLayout().then((layout) => {
      if (!cancelled) setSidebarLayout(layout);
    });
    const onUpdated = () => {
      setSidebarLayout(getCachedSidebarLayout());
      fetchSidebarLayout().then((layout) => {
        if (!cancelled) setSidebarLayout(layout);
      });
    };
    window.addEventListener('sidebarLayoutUpdated', onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('sidebarLayoutUpdated', onUpdated);
    };
  }, []);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (!isSettingsSection(t)) return;
    if (t === 'application' && !isAdmin) {
      setActiveTab('profile');
      setSearchParams({}, { replace: true });
      return;
    }
    setActiveTab(t);
  }, [searchParams, isAdmin, setSearchParams]);

  const handleTabChange = (value: SettingsSection) => {
    if (value === 'application' && !isAdmin) return;
    setActiveTab(value);
    setSearchParams(value === 'profile' ? {} : { tab: value }, { replace: true });
  };

  const [profileData, setProfileData] = useState<ProfileData>({
    userEmail: '',
    userFirstName: '',
    userLastName: '',
    userDisplayName: '',
  });

  const [passwordData, setPasswordData] = useState<PasswordData>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setProfileData({
        userEmail: user.userEmail || '',
        userFirstName: user.userFirstName || '',
        userLastName: user.userLastName || '',
        userDisplayName: user.userDisplayName || '',
      });
    }
  }, [user]);

  const handleProfileChange = (field: keyof ProfileData) => (
    event: { target: { value: string } }
  ) => {
    setProfileData({ ...profileData, [field]: event.target.value });
    if (profileErrors[field]) {
      setProfileErrors({ ...profileErrors, [field]: '' });
    }
  };

  const handlePasswordChange = (field: keyof PasswordData) => (
    event: { target: { value: string } }
  ) => {
    setPasswordData({ ...passwordData, [field]: event.target.value });
    if (passwordErrors[field]) {
      setPasswordErrors({ ...passwordErrors, [field]: '' });
    }
  };

  const validateProfile = (): boolean => {
    const errors: Record<string, string> = {};

    if (!profileData.userEmail.trim()) {
      errors.userEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.userEmail)) {
      errors.userEmail = 'Please enter a valid email address';
    }

    if (!profileData.userFirstName.trim()) {
      errors.userFirstName = 'First name is required';
    }

    if (!profileData.userLastName.trim()) {
      errors.userLastName = 'Last name is required';
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = (): boolean => {
    const errors: Record<string, string> = {};

    if (!passwordData.oldPassword) {
      errors.oldPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProfile()) {
      return;
    }

    setLoading(true);

    try {
      await api.patch('/profile', profileData);
      await refreshUser();
      showSuccess('Profile updated successfully');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to update profile';
      showError(errorMessage);

      if (error?.response?.data?.errors) {
        setProfileErrors(error.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      await api.patch('/profile/change-password', {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });
      showSuccess('Password changed successfully');
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors({});
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to change password';
      showError(errorMessage);

      if (error?.response?.data?.errors) {
        setPasswordErrors(error.response.data.errors);
      } else if (errorMessage.includes('Invalid old password')) {
        setPasswordErrors({ oldPassword: 'Current password is incorrect' });
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength =
    passwordData.newPassword.length === 0
      ? 0
      : passwordData.newPassword.length < 6
        ? 1
        : passwordData.newPassword.length < 10
          ? 2
          : /[A-Z]/.test(passwordData.newPassword) && /[0-9]/.test(passwordData.newPassword)
            ? 4
            : 3;

  const strengthLabel = ['', 'Too short', 'Fair', 'Good', 'Strong'][passwordStrength];
  const strengthColor = [
    '',
    'bg-rose-500',
    'bg-amber-500',
    'bg-sky-500',
    'bg-emerald-500',
  ][passwordStrength];

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-4 sm:p-6">
      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        {/* Section nav */}
        <nav className="lg:sticky lg:top-4 lg:self-start">
          <div
            className={cn(
              'flex gap-1 overflow-x-auto rounded-xl border p-1.5 lg:flex-col',
              colors.surface,
              colors.border
            )}
          >
            {sections.map((section) => {
              const isActive = activeTab === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => handleTabChange(section.id)}
                  className={cn(
                    'flex min-w-[9.5rem] items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors lg:min-w-0',
                    isActive
                      ? 'bg-teal-50 text-teal-900 ring-1 ring-teal-200/80'
                      : cn(colors.textSecondary, colors.interactiveHover)
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                      isActive ? 'bg-teal-600 text-white' : cn(colors.surfaceHover, colors.textMuted)
                    )}
                  >
                    {section.icon}
                  </span>
                  <span className="min-w-0">
                    <span className={cn('block text-sm font-medium', isActive ? 'text-teal-900' : colors.text)}>
                      {section.label}
                    </span>
                    <span className={cn('mt-0.5 hidden text-xs lg:block', isActive ? 'text-teal-700/80' : colors.textMuted)}>
                      {section.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content panel */}
        <div className={cn('overflow-hidden rounded-2xl border', colors.surface, colors.border)}>
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit}>
              <div className="space-y-6 p-5 sm:p-7">
                <div>
                  <h2 className={cn('text-base font-semibold', colors.text)}>Personal information</h2>
                  <p className={cn('mt-1 text-sm', colors.textMuted)}>
                    These details appear across the app and in shared records.
                  </p>
                </div>

                <div className="grid gap-5">
                  <div>
                    <Label htmlFor="userEmail" required>
                      Email address
                    </Label>
                    <TextInput
                      id="userEmail"
                      name="userEmail"
                      type="email"
                      value={profileData.userEmail}
                      onChange={handleProfileChange('userEmail')}
                      placeholder="you@company.com"
                      required
                      error={profileErrors.userEmail}
                      autoComplete="email"
                      className="mt-1.5"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="userFirstName" required>
                        First name
                      </Label>
                      <TextInput
                        id="userFirstName"
                        name="userFirstName"
                        type="text"
                        value={profileData.userFirstName}
                        onChange={handleProfileChange('userFirstName')}
                        placeholder="First name"
                        required
                        error={profileErrors.userFirstName}
                        autoComplete="given-name"
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="userLastName" required>
                        Last name
                      </Label>
                      <TextInput
                        id="userLastName"
                        name="userLastName"
                        type="text"
                        value={profileData.userLastName}
                        onChange={handleProfileChange('userLastName')}
                        placeholder="Last name"
                        required
                        error={profileErrors.userLastName}
                        autoComplete="family-name"
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="userDisplayName">Display name</Label>
                    <TextInput
                      id="userDisplayName"
                      name="userDisplayName"
                      type="text"
                      value={profileData.userDisplayName || ''}
                      onChange={handleProfileChange('userDisplayName')}
                      placeholder="How others see you"
                      helperText="Optional. Shown instead of your full name when set."
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-center justify-end gap-3 border-t px-5 py-4 sm:px-7',
                  colors.border,
                  colors.headerBg
                )}
              >
                <Button type="submit" variant="default" size="default" loading={loading} disabled={loading}>
                  Save profile
                </Button>
              </div>
            </form>
          )}

          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-6 p-5 sm:p-7">
                <div>
                  <h2 className={cn('text-base font-semibold', colors.text)}>Change password</h2>
                  <p className={cn('mt-1 text-sm', colors.textMuted)}>
                    Use a unique password you do not reuse elsewhere.
                  </p>
                </div>

                <div
                  className={cn(
                    'rounded-xl border px-4 py-3 text-sm',
                    colors.border,
                    colors.headerBg,
                    colors.textSecondary
                  )}
                >
                  After changing your password you will stay signed in on this device.
                </div>

                <div className="mx-auto grid max-w-lg gap-5">
                  <div>
                    <Label htmlFor="oldPassword" required>
                      Current password
                    </Label>
                    <TextInput
                      id="oldPassword"
                      name="oldPassword"
                      type="password"
                      value={passwordData.oldPassword}
                      onChange={handlePasswordChange('oldPassword')}
                      placeholder="Enter current password"
                      required
                      error={passwordErrors.oldPassword}
                      autoComplete="current-password"
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label htmlFor="newPassword" required>
                      New password
                    </Label>
                    <TextInput
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange('newPassword')}
                      placeholder="At least 6 characters"
                      required
                      error={passwordErrors.newPassword}
                      autoComplete="new-password"
                      minLength={6}
                      className="mt-1.5"
                    />
                    {passwordData.newPassword.length > 0 && (
                      <div className="mt-2.5 space-y-1.5">
                        <div className="flex gap-1">
                          {[1, 2, 3, 4].map((step) => (
                            <div
                              key={step}
                              className={cn(
                                'h-1 flex-1 rounded-full transition-colors',
                                passwordStrength >= step ? strengthColor : 'bg-slate-200'
                              )}
                            />
                          ))}
                        </div>
                        <p className={cn('text-xs', colors.textMuted)}>{strengthLabel}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" required>
                      Confirm new password
                    </Label>
                    <TextInput
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange('confirmPassword')}
                      placeholder="Repeat new password"
                      required
                      error={passwordErrors.confirmPassword}
                      autoComplete="new-password"
                      minLength={6}
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-center justify-end gap-3 border-t px-5 py-4 sm:px-7',
                  colors.border,
                  colors.headerBg
                )}
              >
                <Button type="submit" variant="default" size="default" loading={loading} disabled={loading}>
                  Update password
                </Button>
              </div>
            </form>
          )}

          {activeTab === 'preferences' && (
            <div className="p-5 sm:p-7">
              <div className="mb-6">
                <h2 className={cn('text-base font-semibold', colors.text)}>Preferences</h2>
                <p className={cn('mt-1 text-sm', colors.textMuted)}>
                  Control tables, dates, theme, and default module views.
                </p>
              </div>
              <Preferences embedded />
            </div>
          )}

          {activeTab === 'menu' && (
            <div className="space-y-6 p-5 sm:p-7">
              <div>
                <h2 className={cn('text-base font-semibold', colors.text)}>Customize menu</h2>
                <p className={cn('mt-1 text-sm', colors.textMuted)}>
                  Reorder sidebar groups and create your own menu sections. Changes are saved to your account.
                </p>
              </div>
              <SidebarCustomizer
                embedded
                baseItems={sidebarBaseItems}
                initialLayout={sidebarLayout}
              />
            </div>
          )}

          {activeTab === 'application' && isAdmin && (
            <div className="p-5 sm:p-7">
              <div className="mb-6">
                <h2 className={cn('text-base font-semibold', colors.text)}>Application</h2>
                <p className={cn('mt-1 text-sm', colors.textMuted)}>
                  Company-wide features, branding, tables, and dashboard layout.
                </p>
              </div>
              <ApplicationSettingsForm embedded />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
